import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { MatOptionSelectionChange } from '@angular/material/core';
import type { IWindowService } from 'angular';
import { Subscription } from 'rxjs';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { BaseGroupType } from 'src/AngularJS/Models/group-type.model';
import type { Group } from 'src/AngularJS/Models/group.model';
import type { FilteredGroups, GroupSelectorDTO, GroupSelectorPostObject } from 'src/AngularJS/Reusables/group-selector.models';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { ClearViewUtils } from 'src/shared/utils';
import { MultiSelectComponent } from '../multi-select/multi-select.component';
import { CvMultiSelectSelectionChangedArgs } from '../multi-select/multi-select.models';
import { CvConfigurationService } from '../services/cv-configuration.service';
import { SingleSelectComponent } from '../single-select/single-select.component';
import { GroupSelectorService } from './group-selector.service';
import { GroupViewBy } from '../models/group-type.model';

@Component({
    selector: 'cv-group-selector',
    templateUrl: './group-selector.component.html',
    styleUrls: ['./group-selector.component.scss']
})
export class GroupSelectorComponent implements OnChanges, OnDestroy {
    @Input() pageName: string;
    @Input() baseGroupType: BaseGroupType;
    @Input() showSelectedToggle: boolean;
    @Input() settingsManager?: SlideoutSettingsManager;
    @Input() selectedGroupsDto: GroupSelectorDTO = null;
    @Input() hasSelectedToggle: boolean;
    @Input() hasMultiSelect: boolean;
    @Input() removeTopLevel = false;
    @Input() direction: 'horizontal' | 'vertical' = 'vertical';
    @Input() specificLevel: number = null;
    @Input() showLabels = true;
    @Input() selectAllOnEmpty = true;
    @Input() removePocFilter = false;
    @Input() creatorId: number; //wallboards uses this instead of loginId for states
    @Input() newSettingsEmp: number[] = null;
    @Input() newSettingsRec: number[] = null;
    @Input() groupViewBy: GroupViewBy;
    @Input() settingViewByLimitsFilters: number;
    @Input() isInfiniteDropdown = false;
    @Input() maxSelectionLimitValue: number;

    @Output() selectedGroupsChanged = new EventEmitter<GroupSelectorDTO>();
    @Output() selectedLevelChanged = new EventEmitter<{ groupType: BaseGroupType; groupLevel: number; groupLevelName: string;}>();
    @Output() selectedGroupsLoaded = new EventEmitter<GroupSelectorDTO>();
    @Output() selectedGroupToggleChanged = new EventEmitter<GroupSelectorDTO>();

    @ViewChildren(MultiSelectComponent) multiSelectComponents: QueryList<MultiSelectComponent>;
    @ViewChildren(SingleSelectComponent) singleSelectComponents: QueryList<SingleSelectComponent>;

    initialized = false;
    groupsByLevel: { [level: number]: FilteredGroups } = null;
    groupTypes: { level: number; name: string; localizedCurrent: string }[] = [];
    prevGroupTypes: { level: number; name: string; localizedCurrent: string }[] = [];
    companyGroupTypeMaxLevels: { [groupType: number]: number } = null;
    groupTypesForFilter: { level: number; name: string }[] = [];
    isLoadingGroups = true;
    lastLevelChanged = 0;
    public teamMap: Map<number, string>;
    public localizationDic: Map<string, string> = new Map();
    selectLocalized = "";
    selectedLevel: number;
    selectedGroups: { [level: number]: number[] } = {
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
    };

    selectedGroup: { [level: number]: number } = {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
    };

    selectedGroupsText: { [level: number]: string } = {
        1: 'All',
        2: 'All',
        3: 'All',
        4: 'All',
        5: 'All',
    };

    hasAll = {
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
    };

    search = {
        1: '',
        2: '',
        3: '',
        4: '',
        5: '',
    };

    private _dateRangeSubscription: Subscription;
    private _otherSelectorSubscription: Subscription;
    private _scrollableSubscription: Subscription;
    private _beforeApplySubscription: Subscription;
    private _groupViewByChangedSubscription: Subscription;
    private _savingPromise: Promise<ApiResponse<GroupSelectorDTO>> = null;
    private _openPromise: Promise<void> = null;
    private _resolveOpenPromise: () => void = null;

    private _allText = "All";
    private _currentText = "Current {0}";
    excludeAll = false;
    agentGroup = false;
    isCXOne: boolean;

    constructor(
        private service: GroupSelectorService,
        private localization: CVLocalizationService,
        readonly scrollDispatcher: ScrollDispatcher,
        private config: CvConfigurationService,
        @Inject('$window') private $window: IWindowService,
    ) {
        localization.GetLocalText("Common", "All", "All").then(res => this._allText = res);
        localization.GetLocalText("Common", "Current_LevelName", "Current {0}").then(res => {
            this._currentText = res;
            this.groupTypes.forEach(gt => gt.localizedCurrent = this._currentText.replace('{0}', gt.name));
        });
        this.isCXOne = this.config.isCxOne;
    }

    // this is a getter that will return the maxSelectionLimit based on the settingViewByLimitsFilters and the selectedLevel
    maxSelectionLimit(level: number) {
        if (this.settingViewByLimitsFilters > 0 && level === this.groupViewBy?.GroupLevel
            && this.baseGroupType === this.groupViewBy?.GroupType) {
            return this.settingViewByLimitsFilters;
        }
        else {
            return 0;
        }
    }

    async initialize() {
        const promisesToWait: Promise<any>[] = [];
        promisesToWait.push(this.localization.GetSection("Common"));
        promisesToWait.push(this.localization.GetSection("Group_Selector"));

        if (this.selectedGroupsDto == null)
            promisesToWait.push(this.loadDTO());
        promisesToWait.push(this.loadGroups());
        if (this.companyGroupTypeMaxLevels == null)
            promisesToWait.push(this.loadCompanyGroupTypeMaxLevels());
        const resolvedPromises = await Promise.all(promisesToWait);

        this.localizationDic = resolvedPromises[1];
        this.selectLocalized = this.localization.getCachedText("Common", "Select", "Select");


        // reload groups when date range changes
        this.groupTypes = [];
        let topLevel = 1;
        let levelCheck = this.selectedGroupsDto.SelectedMaxLevel;
        for (const level of Object.keys(this.groupsByLevel)) {
            const levelNum = Number(level);

            if (levelNum > topLevel)
                topLevel = levelNum;

            // check if selected level is less than the actual number of groups so all groups load correctly
            if (levelNum > this.selectedGroupsDto.SelectedMaxLevel)
                levelCheck++;


            // verify we are within the restricted level
            if (this.selectedGroupsDto.SelectedMinLevel > levelNum || levelNum > levelCheck)
                continue;

            this.groupTypes.push({
                level: levelNum,
                name: this.groupsByLevel[levelNum].GroupLevelName,
                localizedCurrent: this._currentText.replace('{0}', this.groupsByLevel[level].GroupLevelName),
            });
        }

        this.groupTypes.reverse();
        // this block of code is happening before the removal of the top level from this.groupTypes so the viewby dropdown includes company for cxone
        if (this.settingsManager) {
            if (this.baseGroupType === BaseGroupType.Employee) {
                this.settingsManager.empGroupTypes = this.groupTypes;

                // If we are on CXone, and the settings manager has loaded the company level (max level), we need to rename it to include the hierarchy type (Employee or Skill)
                if (this.config.isCxOne && this.settingsManager.empGroupTypes[0].level === this.companyGroupTypeMaxLevels[this.baseGroupType])
                    this.settingsManager.empGroupTypes[0].name = this.settingsManager.empGroupTypes[0].name + " (Employee hierarchy)";
                this._otherSelectorSubscription = this.settingsManager.onSelectedRecGroupsChanged.subscribe(() =>
                    this.onOtherSelectorChanged()
                );
            }
            else if (this.baseGroupType === BaseGroupType.Recording) {
                this.settingsManager.recGroupTypes = this.groupTypes;

                // If we are on CXone, and the settings manager has loaded the company level (max level), we need to rename it to include the hierarchy type (Employee or Skill)
                if (this.config.isCxOne && this.settingsManager.recGroupTypes[0].level === this.companyGroupTypeMaxLevels[this.baseGroupType])
                    this.settingsManager.recGroupTypes[0].name = this.settingsManager.recGroupTypes[0].name + " (Skill hierarchy)";
                this._otherSelectorSubscription = this.settingsManager.onSelectedEmpGroupsChanged.subscribe(() =>
                    this.onOtherSelectorChanged()
                );
            }
        }

        // if removeTopLevel is set to true, remove the top level from the groupTypes at the company level. Because this loads in the user's access to the levels,
        // if the top level isn't the company level, don't remove it
        if (this.removeTopLevel && topLevel <= this.companyGroupTypeMaxLevels[this.baseGroupType]) {
            this.groupTypes = this.groupTypes.filter(gt => gt.level !== this.companyGroupTypeMaxLevels[this.baseGroupType]);
        }

        if (this.removePocFilter) {
            this.groupTypes = this.groupTypes.filter(gt => gt.level !== 1);
        }

        if (this.settingsManager) {
            // currently only used by report widget - report selected gives info on which levels should be displayed
            if (this.baseGroupType === BaseGroupType.Employee && this.settingsManager.settingEmpLevels) {
                this.groupTypes = this.groupTypes.filter(gt => this.settingsManager.settingEmpLevels.includes(gt.level));
            }
            if (this.baseGroupType === BaseGroupType.Recording && this.settingsManager.settingRecLevels) {
                this.groupTypes = this.groupTypes.filter(gt => this.settingsManager.settingRecLevels.includes(gt.level));
            }

            if (this.hasSelectedToggle) {
                this.selectedLevelChanged.emit({
                    groupType: this.baseGroupType,
                    groupLevel: this.selectedGroupsDto.SelectedGroupLevel,
                    groupLevelName: this.groupTypes.find(gt => gt.level === this.selectedGroupsDto.SelectedGroupLevel)?.name,
                });
            }

            this._dateRangeSubscription = this.settingsManager.onSelectedDateRangeChanged.subscribe(() => this.onDateRangeChanged());

            this._beforeApplySubscription = this.settingsManager.beforeSettingsApplied.subscribe(() => {
                if (this._savingPromise !== null) {
                    this.settingsManager.waitBeforeApplying.push(this._savingPromise);
                } else if (this._openPromise !== null) { // selector is open and will likely save after closing
                    this.settingsManager.waitBeforeApplying.push(this._openPromise);
                    this.multiSelectComponents?.forEach(c => c.closeDropdown());
                    this.singleSelectComponents?.forEach(c => c.closeDropdown());
                }
            });

            this._groupViewByChangedSubscription = this.settingsManager.onSelectedGroupViewByChanged.subscribe(() => {
                this.groupViewBy = {
                    ...this.groupViewBy,
                    "GroupType": this.settingsManager?.selectedToggle
                };
                this.groupViewBy.GroupLevel = this.settingsManager?.selectedLevel;
                const selectedLevel = this.selectedGroupsDto.SelectedGroupLevel;
                // Limit the number of groups to be selected for the group view by selector
                if (selectedLevel === this.groupViewBy?.GroupLevel && this.baseGroupType === this.groupViewBy?.GroupType) {
                    if (this.settingViewByLimitsFilters > 0) {
                        const currentGroups = this.selectedGroups[selectedLevel] || [];
                        this.selectedGroups[selectedLevel] = currentGroups.slice(0, this.settingViewByLimitsFilters);
                        this.hasAll[selectedLevel] =
                            this.selectedGroups[selectedLevel]?.length === this.groupsByLevel[selectedLevel]?.Groups?.length;
                        this.selectedLevel = selectedLevel;
                        this.saveSelectedGroups();
                    }
                }
            });
        }
        this.updateSelectorsFromDTO();
        this.initialized = true;
        this.selectedGroupsLoaded?.emit(this.selectedGroupsDto);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.newSettingsEmp || changes.newSettingsRec) {
            if (this.baseGroupType === BaseGroupType.Employee) {
                this.settingsManager.settingEmpLevels = this.newSettingsEmp;
            }
            if (this.baseGroupType === BaseGroupType.Recording) {
                this.settingsManager.settingRecLevels = this.newSettingsRec;
            }
            if (this.initialized) { // means we switched reports in settings modal
                this.initialize();
            }
        }

        if (changes.selectedGroupsDto && !changes.selectedGroupsDto.isFirstChange() && this.initialized) {
            this.updateSelectorsFromDTO();
        }

        if (changes.selectedGroupToggleChanged && changes.selectedGroupToggleChanged.isFirstChange())
            this.saveSelectedGroups(0, this.hasSelectedToggle);

        if (changes.pageName && changes.pageName.isFirstChange()) { // inputs ready
            this.initialize();
        }
    }

    ngOnDestroy() {
        this._dateRangeSubscription?.unsubscribe();
        this._otherSelectorSubscription?.unsubscribe();
        this._scrollableSubscription?.unsubscribe();
    }

    async toggleGroups() {
        await this.loadDTO();
        await this.loadGroups(true); // Load and update groups
        let noneSelected: boolean = this.getSelectedGroupsByLevel(this.selectedLevel).length === 0;
        let allSelected: boolean = this.selectedGroups[this.selectedLevel].length === (this.groupsByLevel[this.selectedLevel]?.Groups?.length - 1);
        let groupChange:  CvMultiSelectSelectionChangedArgs = { 
            value: this.getSelectedGroupsByLevel(this.selectedLevel),
            wasSelectAll: allSelected,
            wasSelectNone: noneSelected
        }; // Event value
        this.onMultiSelectValueChanged(this.selectedLevel, groupChange); // Update multi-select values
        this.updateSelectorsFromDTO();
        this.saveSelectedGroups(); // Save changes
    }

    async updateSelectorsFromDTO() {
        if (this.isLoadingGroups) {
            // this method will be called again after groups are done loading
            return;
        }

        if (!this.hasSelectedToggle) {
            this.selectedLevel = this.selectedGroupsDto.SelectedMinLevel;
        } else {
            this.selectedLevel = this.selectedGroupsDto.SelectedGroupLevel;
        }

        for (let level = 1; level <= 5; level++) {
            let dtoSelectedGroupsForLevel: number[] = this.selectedGroupsDto[`_SelectedGroup${level}IDList`]?.length > 0 ?
                [ ...this.selectedGroupsDto[`_SelectedGroup${level}IDList`] ] : []; // a copy of the currently saved selected groups for this level

            const groupsAvailableInLevelArr = this.groupsByLevel[level]?.Groups?.map(grp => grp.GroupID).filter(g => g !== -1) ?? [];
            const groupsAvailableInLevel = new Set(groupsAvailableInLevelArr); // the group ids that are available to select in this level

            // unselect groups that aren't available to select anymore
            for (let i = 0; i < dtoSelectedGroupsForLevel.length; i++) {
                const groupId = dtoSelectedGroupsForLevel[i];
                if (groupId > 0 && !groupsAvailableInLevel.has(groupId)) {
                    dtoSelectedGroupsForLevel.splice(i, 1);
                    i--;
                }
            }

            // select all if none are selected
            if (dtoSelectedGroupsForLevel.length <= 0) {
                dtoSelectedGroupsForLevel = [0];
            }

            // check for 'All'
            if(dtoSelectedGroupsForLevel[0] === 0 && this.selectAllOnEmpty && !this.excludeAll) {
                this.hasAll[level] = true;
                dtoSelectedGroupsForLevel = [0].concat(Array.from(groupsAvailableInLevel));
            } else {
                this.hasAll[level] = false;
            }

            // Limit the number of groups to be selected for the group view by selector
            if (level === this.groupViewBy?.GroupLevel && this.baseGroupType === this.groupViewBy?.GroupType && this.settingViewByLimitsFilters > 0) {
                dtoSelectedGroupsForLevel = dtoSelectedGroupsForLevel.slice(0, this.settingViewByLimitsFilters + 1);
            }

            // update selected groups model
            if (this.hasMultiSelect)
                this.selectedGroups[level] = dtoSelectedGroupsForLevel.filter(g => g !== 0);
            else
                this.selectedGroup[level] = dtoSelectedGroupsForLevel[0];
            this.selectedGroupsText[level] = this.getSelectorDisplay(level);
        }

    }

    async loadDTO() {
        const response = await this.service.loadGroupSelector(this.pageName, this.baseGroupType);
        this.selectedGroupsDto = response.data;
    }

    async loadGroups(fromToggle?) {
        this.isLoadingGroups = true;
        try {
            const response = await this.service.loadFilteredGroups(this.pageName, this.baseGroupType, this.specificLevel, this.selectedGroupsDto?.toggleGroupData);
            for (const groupLevel of Object.values(response.data)) {
                // Add 'All' option if single-select with view by in PM
                if (!this.hasMultiSelect &&
                    (this.config.isCxOne ||
                    (!this.config.isCxOne && this.showSelectedToggle))) {
                    groupLevel.Groups.unshift({
                        GroupID: 0,
                        GroupName: this._allText,
                    } as Group);
                }
                else if (!this.hasMultiSelect)
                    this.excludeAll = true;

                if (!this.config.isCxOne) {
                    groupLevel.Groups.push({
                        GroupID: -1,
                        GroupName: this._currentText.replace('{0}', groupLevel.GroupLevelName),
                    } as Group);
                }
            }
            this.groupsByLevel = response.data;
            this.teamMap = new Map<number, string>();
            this.groupsByLevel[2]?.Groups.forEach(g => this.teamMap.set(g.GroupID, g.GroupName));
            if (fromToggle)
                this.getSelectedGroupsByLevel(this.selectedLevel);
        }
        finally {
            this.isLoadingGroups = false;
        }
    }

    /** Load max levels for emp/rec group types. Stored in a key-value-pair with key being the integer value for the BaseGroupType enum and the value being the highest level number */
    async loadCompanyGroupTypeMaxLevels() {
        const response = await this.service.loadCompanyGroupTypeMaxLevels();
        this.companyGroupTypeMaxLevels = response;
    }

    async saveSelectedGroups(triggeredByLevel: number = 0, setSelectedToggle = false) {
        const saveObject: GroupSelectorPostObject = {};
        if (this.specificLevel) {
            saveObject[`selectedGroup${this.specificLevel}Ids`] = this.getSelectedGroupsByLevel(this.specificLevel);
        } else {
            saveObject.selectedGroup1Ids = this.getSelectedGroupsByLevel(1);
            saveObject.selectedGroup2Ids = this.getSelectedGroupsByLevel(2);
            saveObject.selectedGroup3Ids = this.getSelectedGroupsByLevel(3);
            saveObject.selectedGroup4Ids = this.getSelectedGroupsByLevel(4);
            saveObject.selectedGroup5Ids = this.getSelectedGroupsByLevel(5);
            saveObject.selectedGroupLevel = this.selectedLevel;
            saveObject.toggleGroupData = this.selectedGroupsDto?.toggleGroupData;
        }

        // check if actually changed
        const oldSaveObject: GroupSelectorPostObject = {};
        if (this.specificLevel) {
            oldSaveObject[`selectedGroup${this.specificLevel}Ids`] = this.selectedGroupsDto[`_SelectedGroup${this.specificLevel}IDList`];
        } else {
            oldSaveObject.selectedGroup1Ids = this.selectedGroupsDto?._SelectedGroup1IDList;
            oldSaveObject.selectedGroup2Ids = this.selectedGroupsDto?._SelectedGroup2IDList;
            oldSaveObject.selectedGroup3Ids = this.selectedGroupsDto?._SelectedGroup3IDList;
            oldSaveObject.selectedGroup4Ids = this.selectedGroupsDto?._SelectedGroup4IDList;
            oldSaveObject.selectedGroup5Ids = this.selectedGroupsDto?._SelectedGroup5IDList;
            oldSaveObject.selectedGroupLevel = this.selectedGroupsDto?.SelectedGroupLevel;
            oldSaveObject.toggleGroupData = !this.selectedGroupsDto?.toggleGroupData;
        }

        if (setSelectedToggle || JSON.stringify(oldSaveObject) !== JSON.stringify(saveObject)) {
            try {
                if (setSelectedToggle)
                    saveObject.selectedToggle = this.hasSelectedToggle;
                this._savingPromise = this.service.saveGroupSelector(this.pageName, this.baseGroupType, saveObject, this.creatorId);
                const response = await this._savingPromise;
                this.lastLevelChanged = triggeredByLevel;

                // refresh DTO and groups
                this.selectedGroupsDto = response.data;
                this.selectedGroupsChanged.emit(this.selectedGroupsDto);
                this.selectedGroupToggleChanged.emit(this.selectedGroupsDto);

                switch (this.baseGroupType) {
                    case BaseGroupType.Employee:
                        this.settingsManager?.onSelectedEmpGroupsChanged.next(this.selectedGroupsDto);
                        this.settingsManager?.onGroupToggleChanged(this.selectedGroupsDto);
                        break;
                    case BaseGroupType.Recording:
                        this.settingsManager?.onSelectedRecGroupsChanged.next(this.selectedGroupsDto);
                        break;
                }
            } finally {
                this._savingPromise = null;
            }

            await this.loadGroups();
            this.updateSelectorsFromDTO();
        }
    }

    getSelectorDisplay(level: number) {
        // show "All" text when all are selected
        if (this.hasAll[level])
            return this._allText;

        // show "Current" text when -1 (use current) is selected
        if ((this.hasMultiSelect && this.selectedGroups[level][0] === -1) || (!this.hasMultiSelect && this.selectedGroup[level] === -1) ) {
            return this.groupTypes.find(gt => gt.level === level)?.localizedCurrent ?? this._currentText.replace('{0}', level.toString());
        }

        // return GroupName for selected group on single select
        if (!this.hasMultiSelect) {
            return this.groupsByLevel[level]?.Groups.find(g => g.GroupID === this.selectedGroup[level])?.GroupName;
        }

        // if multi are selected display a truncated list
        if (this.selectedGroups[level].length > 1) {
            const groups: Group[] = this.groupsByLevel[level].Groups;
            const groupsToJoin = this.selectedGroups[level]
                .slice(0, 10) // max 10 iterations for performance
                .map(id => groups.find(g => g.GroupID === id)?.GroupName ?? '');
            return ClearViewUtils.joinWithMax(groupsToJoin, 15, ', ', this.selectedGroups[level].length);
        }

        // if none are selected display empty string
        if (this.selectedGroups[level].length < 1)
            return "";

        // find selected group
        const groupId = this.selectedGroups[level][0];
        const group = this.groupsByLevel[level].Groups.find(g => g.GroupID === groupId);
        if (group)
            return group.GroupName;
        else { // group no longer available, select All again
            this.hasAll[level] = false; // set to false for it to toggle
            this.onSelectAllToggled(level);
            return "";
        }
    }

    async selectLevel(level: number) {
        this.selectedLevel = level;

        if (!this.hasSelectedToggle) {
            this.hasSelectedToggle = true;
        }

        let saveSelectedToggle = this.hasSelectedToggle;

        this.selectAllOnLowerLevels(level - 1);
        await this.saveSelectedGroups(level, saveSelectedToggle);

        // This prevents error message when the settings are being applied from Dashboard Filter.
        if (this.pageName) {
            this.$window.DashboardSettings.ReloadMetrics(this.pageName, null,
                result => $('.metricSelectorContainer').html($(result).find(".metricSelectorContainer").html()),
                err => this.$window.showErrorMessage(err.get_message())
            );
        }

        this.settingsManager.showInitialLevelName = false
        this.selectedLevelChanged.emit({
            groupType: this.baseGroupType,
            groupLevel: this.selectedLevel,
            groupLevelName: this.groupTypes.find(gt => gt.level === this.selectedLevel).name,
        });

        this.settingsManager?.onSelectedGroupViewByChanged.next({
            GroupType: this.baseGroupType,
            GroupLevel: this.selectedLevel,
        });
    }

    async selectAllOnLowerLevels(level: number) {
        if (!this.hasMultiSelect) {
            this.selectedGroup[level] = 0;
            this.hasAll[level] = true;
            return;
        }

        for (; level >= 1; level--) {
            if (!this.hasAll[level]) {
                const groupsAvailableInLevel = this.groupsByLevel[level]?.Groups?.map(grp => grp.GroupID) ?? [];
                this.hasAll[level] = true;

                // update selected groups model
                this.selectedGroups[level] = [0].concat(groupsAvailableInLevel);
            }
        }
    }

    onSelectedViewByChanged() {
        this.groupViewBy.GroupType = this.settingsManager?.selectedToggle;
        this.groupViewBy.GroupLevel = this.settingsManager?.selectedLevel;
        const selectedLevel = this.selectedGroupsDto.SelectedGroupLevel;
        // Limit the number of groups to be selected for the group view by selector
        if (selectedLevel === this.groupViewBy?.GroupLevel && this.baseGroupType === this.groupViewBy?.GroupType) {
            if (this.settingViewByLimitsFilters > 0) {
                const currentGroups = this.selectedGroups[selectedLevel] || [];
                this.selectedGroups[selectedLevel] = currentGroups.slice(0, this.settingViewByLimitsFilters);
                const allLength = Math.min(this.groupsByLevel[selectedLevel]?.Groups?.length, this.settingViewByLimitsFilters);
                this.hasAll[selectedLevel] = this.selectedGroups[selectedLevel]?.length === allLength;
                this.selectedLevel = selectedLevel;
                this.onMultiSelectValueChanged(selectedLevel, {
                    value: this.selectedGroups[selectedLevel],
                    wasSelectAll: this.hasAll[selectedLevel],
                    wasSelectNone: this.selectedGroups[selectedLevel]?.length === 0
                });
                this.onGroupSelectorClose(selectedLevel);
            }
        }
    }

    async onDateRangeChanged() {
        this.lastLevelChanged = 0;
        await this.loadGroups();
        this.updateSelectorsFromDTO();
    }

    async onOtherSelectorChanged() {
        this.lastLevelChanged = 0;
        await this.loadGroups();
        this.updateSelectorsFromDTO();
    }

    onSelectAllToggled(level: number) {
        if (!this.hasMultiSelect) {
            if (!this.hasAll[level]) {
                this.hasAll[level] = true;
                this.selectedGroup[level] = 0;
            }
        } else {
            if (!this.hasAll[level]) {
                this.selectedGroups[level] = [0].concat(this.groupsByLevel[level].Groups.map(grp => grp.GroupID));
            } else {
                this.selectedGroups[level] = [];
            }
            this.hasAll[level] = !this.hasAll[level];
        }

        this.selectedGroupsText[level] = this.getSelectorDisplay(level);
    }

    onSingleSelectValueChanged(level: number, newValue: number) {
        this.selectedGroup[level] = newValue;
        this.hasAll[level] = this.selectedGroup[level] === 0;
        this.selectedGroupsText[level] = this.getSelectorDisplay(level);
    }

    onMultiSelectValueChanged(level: number, change: CvMultiSelectSelectionChangedArgs) {
        if(change.value.length === 0 && this.maxSelectionLimit(level) > 0) {
            return;
        }
        const oldValue = this.selectedGroups[level];
        this.selectedGroups[level] = change.value;

        if (this.selectedGroups[level].length > 1 || change.wasSelectAll) {
            const indexOfCurrent = this.selectedGroups[level].indexOf(-1);
            if (indexOfCurrent >= 0) {
                if (change.wasSelectAll || oldValue?.indexOf(-1) >= 0) {
                    // all were selected which included current OR current was selected and a new item was added
                    // SO current shouldn't be selected anymore
                    this.selectedGroups[level] = this.selectedGroups[level].filter(g => g !== -1);
                } else {
                    // current was added, select only current
                    this.selectedGroups[level] = [ -1 ];
                }
            }
        }

        // check if all selected
        const hasCurrent = !this.config.isCxOne;
        if ((this.selectedGroups[level].length !== this.groupsByLevel[level].Groups.length - (hasCurrent ? 1 : 0)) || (this.groupsByLevel[level].Groups.length - (hasCurrent ? 1 : 0) == 1 && !change.wasSelectAll)) { // subtract 1 because of Current being added to the list
            this.hasAll[level] = false;
        } else {
            this.hasAll[level] = true;
        }


        // } else {
        //     this.hasAll[level] = this.selectedGroup[level] === 0;
        // }

        this.selectedGroupsText[level] = this.getSelectorDisplay(level);
    }

    async onGroupSelectorOpen(level: number) {
        this._openPromise = new Promise(resolve => this._resolveOpenPromise = resolve);
        if (level === 1 && this.baseGroupType == 0) {
            this.agentGroup = true;
        }
        else {
            this.agentGroup = false;
        }
    }

    async onGroupSelectorClose(level: number) {
        if ((this.hasMultiSelect && this.selectedGroups[level].length === 0) && this.selectAllOnEmpty) {
            this.selectedGroups[level] = this.groupsByLevel[level].Groups.filter(g => g.GroupID !== -1).map(g => g.GroupID);
            this.hasAll[level] = true;
            this.selectedGroupsText[level] = this.getSelectorDisplay(level);
        }

        try {
            await this.saveSelectedGroups(level);
        } finally {
            this._resolveOpenPromise?.();
            this._resolveOpenPromise = null;
            this._openPromise = null;
        }
    }

    private getSelectedGroupsByLevel(level: number) {
        if (this.selectedLevel > level) // this level not in selector
            return [];

        if (this.hasAll[level] && this.selectAllOnEmpty) // all selected
            return [0];

        if (this.hasMultiSelect && !this.selectAllOnEmpty && this.selectedGroups[level].length === 0)
            return [0];

        if (this.hasMultiSelect)
            return this.selectedGroups[level];
        else
            return [ this.selectedGroup[level] ];
    }

    getLocalizationString(key, defaultValue) {
        if (this.localizationDic && this.localizationDic.has(key))
            return this.localizationDic.get(key);
        return defaultValue;
    }
}
